import { useEffect, useRef, useState } from 'react';
import ParticleCanvas from './ParticleCanvas';

type OsTab = 'linux' | 'windows';

const INSTALL_COMMANDS: Record<'linux' | 'windows', string> = {
  linux: 'curl -sSL https://lum.id/start | bash',
  windows: 'iwr https://lum.id/install.ps1 -useb | iex',
};

const TYPEWRITER_PHRASES = [
  'Furnace & Marketplace for Auto Research',
  'Hybrid Compute on Tap, Cloud + Edge',
  'Human Experts in the Loop',
  'Agentic Lakehouse + Analytics',
  'Deliver results, not complication',

];


function useTypewriter(phrases: string[]) {
  const [displayed, setDisplayed] = useState('');
  const [phraseIdx, setPhraseIdx] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const current = phrases[phraseIdx];
    const tick = () => {
      if (!isDeleting) {
        if (displayed.length < current.length) {
          setDisplayed(current.slice(0, displayed.length + 1));
          timeoutRef.current = setTimeout(tick, 55 + Math.random() * 30);
        } else {
          timeoutRef.current = setTimeout(() => setIsDeleting(true), 1800);
        }
      } else {
        if (displayed.length > 0) {
          setDisplayed(current.slice(0, displayed.length - 1));
          timeoutRef.current = setTimeout(tick, 28 + Math.random() * 15);
        } else {
          setIsDeleting(false);
          setPhraseIdx((i) => (i + 1) % phrases.length);
        }
      }
    };
    timeoutRef.current = setTimeout(tick, 80);
    return () => { if (timeoutRef.current) clearTimeout(timeoutRef.current); };
  }, [displayed, isDeleting, phraseIdx, phrases]);

  return displayed;
}

export default function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);
  const [activeTab, setActiveTab] = useState<OsTab>('linux');
  const [copied, setCopied] = useState(false);
  const [scanDone, setScanDone] = useState(false);
  const typedText = useTypewriter(TYPEWRITER_PHRASES);

  useEffect(() => {
    const t = setTimeout(() => setIsLoaded(true), 100);
    const s = setTimeout(() => setScanDone(true), 1200);
    return () => { clearTimeout(t); clearTimeout(s); };
  }, []);

  const handleCopy = () => {
    navigator.clipboard.writeText(INSTALL_COMMANDS[activeTab]);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <section className="min-h-screen flex items-center justify-center relative overflow-x-hidden bg-slate-900">
      {/* Particle constellation background */}
      <ParticleCanvas />

      {/* Subtle grid overlay */}
      <div className="absolute inset-0 dark-grid opacity-20" />

      {/* Floating orbs */}
      <div className="absolute top-20 right-20 w-20 h-20 light-orb rounded-full animate-dramatic-float"></div>
      <div className="absolute bottom-32 left-20 w-16 h-16 light-orb rounded-full animate-dramatic-float animation-delay-1000"></div>
      <div className="absolute top-1/3 left-1/3 w-12 h-12 light-orb rounded-full animate-slow-bounce animation-delay-500"></div>

      {/* Particles & twinkling stars */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="lumid-particle w-2 h-2 top-1/6 left-1/4 animation-delay-200"></div>
        <div className="lumid-particle w-3 h-3 bottom-1/4 right-1/6 animation-delay-800"></div>
        <div className="lumid-particle w-1.5 h-1.5 top-2/3 right-1/3 animation-delay-1500"></div>
        <div className="lumid-particle w-2.5 h-2.5 top-1/2 left-1/6 animation-delay-1200"></div>
        <div className="lumid-particle-blue w-1 h-1 top-1/5 right-1/5 animation-delay-300"></div>
        <div className="lumid-particle-purple w-2 h-2 bottom-1/3 left-1/3 animation-delay-600"></div>
        <div className="lumid-particle-cyan w-1.5 h-1.5 top-3/4 left-1/5 animation-delay-900"></div>
        <div className="lumid-particle-indigo w-1 h-1 top-1/3 right-2/5 animation-delay-1100"></div>
        <div className="lumid-particle-blue w-2.5 h-2.5 bottom-1/6 right-1/4 animation-delay-1400"></div>
        <div className="lumid-particle w-1 h-1 top-1/4 left-2/3 animation-delay-1700"></div>
        <div className="lumid-particle-purple w-1.5 h-1.5 bottom-2/5 left-1/8 animation-delay-2000"></div>
        <div className="lumid-particle-cyan w-1 h-1 top-2/5 right-1/8 animation-delay-400"></div>
        <div className="absolute w-0.5 h-0.5 bg-white rounded-full top-1/6 left-1/2 animate-twinkle"></div>
        <div className="absolute w-1 h-1 bg-slate-300 rounded-full bottom-1/5 right-1/3 animate-twinkle animation-delay-500"></div>
        <div className="absolute w-0.5 h-0.5 bg-white rounded-full top-3/5 left-1/4 animate-twinkle animation-delay-800"></div>
        <div className="absolute w-0.5 h-0.5 bg-slate-300 rounded-full top-1/8 right-1/6 animate-twinkle animation-delay-1200"></div>
      </div>

      {/* Radial glow behind terminal */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-teal-500/5 rounded-full blur-[100px] pointer-events-none" />

      {/* Fade-in reveal overlay */}
      <div
        className={`absolute inset-0 z-20 pointer-events-none bg-slate-900 transition-opacity duration-700 ease-out ${scanDone ? 'opacity-0' : 'opacity-100'}`}
      />

      {/* Content */}
      <div className={`relative z-10 w-full max-w-2xl mx-auto px-6 py-20 text-center transition-all duration-700 ${
        isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
      }`}>

        {/* Heading */}
        <h1
          className="text-3xl md:text-4xl font-artistic font-light text-white mb-4 mt-10 leading-normal pb-1"
        >
          <span className="whitespace-nowrap"><span className="text-slate-400">&nbsp; Light up ideas with</span> <span className="lumid-text-shine inline-block">LumidOS</span>
          <a
            href="https://github.com/mlsys-io/LumidOS"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center w-8 h-8 ml-3 text-slate-400 hover:text-white transition-colors duration-300 cursor-pointer align-middle"
            title="LumidOS on GitHub"
          >
            <i className="ri-github-fill text-2xl"></i>
          </a></span>
        </h1>

        {/* Subtitle */}
        <p className="text-sm text-slate-500 font-light mb-5 leading-relaxed mt-6 whitespace-nowrap">
          AI-driven auto-research for everyone.
        </p>

        {/* Install block */}
        <div className="inline-block mx-auto">
          {/* Tabs */}
          <div className="flex items-center border-b border-slate-700/50">
            {([
              { id: 'linux', label: 'Linux & macOS' },
              { id: 'windows', label: 'Windows' },
            ] as { id: OsTab; label: string }[]).map(({ id, label }) => (
              <button
                key={id}
                onClick={() => setActiveTab(id)}
                className={`px-4 py-2 text-xs font-medium whitespace-nowrap cursor-pointer transition-all duration-200 border-b-2 -mb-px ${
                  activeTab === id
                    ? 'border-teal-400 text-teal-300'
                    : 'border-transparent text-slate-600 hover:text-slate-400'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Panel */}
          <div className="flex flex-col bg-slate-900/60 border border-t-0 border-slate-700/50 rounded-b-lg px-5 py-3.5 font-mono gap-2">
            {/* Typewriter line */}
            <div className="text-left text-sm">
              <span className="text-slate-500 mr-2">//</span>
              <span className="text-teal-300">
                {typedText}
                <span className="inline-block w-[2px] h-[13px] bg-teal-400 ml-0.5 align-middle animate-[blink_1s_step-end_infinite]"></span>
              </span>
            </div>

            {/* Install command line */}
            <div className="flex items-center">
              <span className="text-slate-600 mr-3 select-none text-xs">$</span>
              {activeTab === 'linux' ? (
                <code className="text-left text-slate-300 text-sm leading-relaxed whitespace-nowrap">
                  <span className="text-teal-300">curl</span>
                  <span className="text-slate-500"> -sSL </span>
                  <span className="text-amber-400">https://lum.id/start</span>
                  <span className="text-slate-500"> | bash</span>
                </code>
              ) : (
                <code className="text-left text-slate-300 text-sm leading-relaxed whitespace-nowrap">
                  <span className="text-teal-300">iwr</span>
                  <span className="text-amber-400"> https://lum.id/install.ps1</span>
                  <span className="text-slate-500"> -useb | iex</span>
                </code>
              )}
              <button
                onClick={handleCopy}
                className="ml-3 w-7 h-7 flex items-center justify-center rounded text-slate-600 hover:text-slate-300 transition-colors duration-200 cursor-pointer flex-shrink-0"
                title="Copy"
              >
                {copied
                  ? <i className="ri-check-line text-emerald-400 text-sm"></i>
                  : <i className="ri-file-copy-line text-sm"></i>
                }
              </button>
            </div>
          </div>
        </div>


      </div>
    </section>
  );
}